import classNames from 'classnames'
import React, { Component, useEffect, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Autocomplete from 'react-google-autocomplete'
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api'
import { Provider, connect } from 'react-redux'
import { store } from '../..'
import { ReactComponent as CompressIcon } from '../../assets/compress.svg'
import { ReactComponent as CSVIcon } from '../../assets/csv.svg'
import { ReactComponent as ExpandIcon } from '../../assets/expand.svg'
import { ReactComponent as ArrowLeftIcon } from '../../assets/left.svg'
import { ReactComponent as SearchIcon } from '../../assets/search.svg'
import {
  getBeginningOfDate,
  getEndOfDate,
  getIconColor,
  isSafari,
  setFullscreen,
} from '../../lib/Helper'
import BorderlessButton from '../../lib/borderless-button/borderless-button'
import Button from '../../lib/button/button'
import Dialog from '../../lib/dialog/dialog'
import UserInfo from '../../models/UserInfo'
import { exportPointsToCsv } from '../../utils/exportPointsToCsv'

// Marker SVGs exist for iconId 0–26; clamp to avoid require() of missing modules
const MAX_MARKER_ICON_ID = 26
import InfoView from './info-view.js'
import MarkerFilters from './marker-filters'
import NewPointModal from './new-point-modal'
import './page-map.css'

export class MapPage extends Component {
  constructor(props) {
    super(props)

    if (!props.company || !props.user) this.back()
    this.API_KEY = 'AIzaSyDxEPjK3OeXbWudv8kLsxCCelMwrNukF7E'

    this.state = {
      locationPointTypes: undefined,
      locationPoints: undefined,
      filters: {},
      showRemoved: undefined, // {from: Date, to: Date}
      isRemovedDialogVisible: false,
      isLoading: false,
      dateRange: undefined, // { from: number, to: number } timestamps for marker filter
      dataBounds: undefined, // { from: number, to: number } min/max dates in data for DatePicker constraints
      searchLocation: undefined, // When search with searchbar
    }

    this.locationPoints = undefined
    this.initialCoordinates = [65.5, 25.0]
  }

  componentDidMount() {
    this.props.project
      .getLocationPointTypes()
      .then((typesArray) => {
        // convert to objects for easier access
        const locationPointTypes = typesArray.reduce((obj, data) => {
          obj[data.id] = data
          return obj
        }, {})
        this.setState({ locationPointTypes })
      })
      .catch((err) => {
        console.error(err)
        alert('Pisteiden lataaminen epäonnistui')
      })

    this.getLocationPoints()
  }

  componentWillUnmount() {
    if (this.props.project) {
      this.props.project.removeListeners()
    }
  }

  back = () => {
    if (!isSafari())
      setFullscreen(false).catch((err) => {
        console.log(err)
      })
    this.props.history.push('/')
  }

  getLocationPoints = () => {
    const handleResult = (error, locationPoints) => {
      if (error) {
        console.error(error)
        alert('Pisteiden lataaminen epäonnistui')
      } else {
        this.locationPoints = locationPoints
        // Compute data bounds for DatePicker constraints
        let minDate = undefined
        let maxDate = undefined
        locationPoints.forEach((point) => {
          const t = new Date(point.dateCreated).getTime()
          if (minDate === undefined || t < minDate) minDate = t
          if (maxDate === undefined || t > maxDate) maxDate = t
        })
        const dataBoundsFrom =
          minDate != null
            ? getBeginningOfDate(new Date(minDate), true)
            : undefined
        const dataBoundsTo =
          maxDate != null ? getEndOfDate(new Date(maxDate), true) : undefined
        // Only set dateRange if not already set (preserve user selection across reloads)
        const { dateRange } = this.state
        const newDateRange =
          dateRange !== undefined
            ? dateRange
            : dataBoundsFrom != null && dataBoundsTo != null
              ? { from: dataBoundsFrom, to: dataBoundsTo }
              : undefined
        this.setState(
          {
            dateRange: newDateRange,
            dataBounds:
              dataBoundsFrom != null && dataBoundsTo != null
                ? { from: dataBoundsFrom, to: dataBoundsTo }
                : undefined,
          },
          () => this.setLocationPoints(),
        )
      }
    }

    this.props.project.removeListeners()
    this.setState({ locationPoints: undefined })
    const { showRemoved } = this.state
    if (showRemoved) {
      this.props.project.listenRemovedLocationPoints(
        showRemoved.from,
        showRemoved.to,
        handleResult,
      )
    } else {
      this.props.project.listenLocationPoints(handleResult)
    }
  }

  // Filters points and then sets to state
  setLocationPoints = () => {
    const { filters, locationPointTypes, dateRange } = this.state
    const locationPoints = this.locationPoints
    if (!locationPoints || !filters)
      return this.setState({ locationPoints, isLoading: false })
    const points = locationPoints.filter((point) => {
      // Filter by date range (creation date within from–to)
      if (dateRange) {
        const pointTime = new Date(point.dateCreated).getTime()
        if (dateRange.from != null && pointTime < dateRange.from) return false
        if (dateRange.to != null && pointTime > dateRange.to) return false
      }

      //Filter by toggle filters
      const isAging =
        locationPointTypes && locationPointTypes[point.type]
          ? locationPointTypes[point.type].aging || false
          : false
      if (
        isAging &&
        isDated(locationPointTypes[point.type].aging, point.dateCreated)
      )
        return filters[`aging_${point.type}`] ? false : true // Remove if filter exists
      return filters[point.type] ? false : true
    })
    this.setState({ locationPoints: points, isLoading: false })
  }

  render() {
    const { project, userId } = this.props
    const {
      locationPoints,
      locationPointTypes,
      filters,
      showRemoved,
      isLoading,
      isRemovedDialogVisible,
      dateRange,
      dataBounds,
    } = this.state
    return (
      <div className="map-page">
        <NavBar
          onBack={this.back}
          title={project.info.name}
          onExportCSV={() => {
            this.setState({ isLoading: true })
            exportPointsToCsv(
              locationPoints,
              locationPointTypes,
              project,
              !!showRemoved,
            )
              .catch((err) => {
                console.error('Failed to export data: ', err)
                alert('Tiedoston lataus epäonnistui')
              })
              .finally(() => {
                this.setState({ isLoading: false })
              })
          }}
        />
        <div className="map-container">
          <MapContainer
            apiKey={this.API_KEY}
            locationPoints={locationPoints}
            locationPointTypes={locationPointTypes}
            initialCoordinates={this.initialCoordinates}
            project={project}
            showRemoved={showRemoved}
            userId={userId}
            isLoading={isLoading}
            setLoading={(loading) => this.setState({ isLoading: loading })}
          />

          <MarkerFilters
            types={locationPointTypes}
            onChange={(filters) =>
              this.setState({ filters }, () => {
                this.setLocationPoints()
              })
            }
            filters={filters}
            onShowRemoved={(removed) => {
              if (removed) {
                const to = getEndOfDate(new Date(), true)
                const lastMonth = new Date()
                lastMonth.setMonth(lastMonth.getMonth() - 1)
                const from = getBeginningOfDate(lastMonth, true)
                this.setState({
                  isRemovedDialogVisible: true,
                  showRemoved: { from: from, to: to },
                })
              } else {
                this.setState({ showRemoved: undefined }, () =>
                  this.getLocationPoints(),
                )
              }
            }}
            showRemoved={showRemoved}
            isLoading={isLoading}
            onDateRangeChange={(dateRange) =>
              this.setState({ dateRange }, () => this.setLocationPoints())
            }
            dateRange={dateRange}
            dataBounds={dataBounds}
          />

          {isRemovedDialogVisible && showRemoved && (
            <RemovedModal
              showRemoved={showRemoved}
              onDone={(values) => {
                this.setState(
                  { showRemoved: values, isRemovedDialogVisible: false },
                  () => this.getLocationPoints(),
                )
              }}
              onCancel={() =>
                this.setState({
                  showRemoved: undefined,
                  isRemovedDialogVisible: false,
                })
              }
            />
          )}
        </div>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    user: state.auth.user,
    userId: state.auth.user.firebaseUser.uid,
    company: state.selectedCompany,
    project: state.selectedProject,
  }
}

export default connect(mapStateToProps)(MapPage)

// Custom hook to detect prevProps - https://stackoverflow.com/questions/53446020/how-to-compare-oldvalues-and-newvalues-on-react-hooks-useeffect
function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const mapContainerStyle = { width: '100%', height: '100%' }

function MapContainer({
  apiKey,
  locationPoints,
  locationPointTypes,
  initialCoordinates,
  project,
  showRemoved,
  userId,
  isLoading,
  setLoading,
}) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: ['geometry', 'drawing', 'places'],
  })

  if (!isLoaded) {
    return <div style={{ height: '100%' }} />
  }

  return (
    <Map
      locationPoints={locationPoints}
      locationPointTypes={locationPointTypes}
      initialCoordinates={initialCoordinates}
      project={project}
      showRemoved={showRemoved}
      userId={userId}
      isLoading={isLoading}
      setLoading={setLoading}
    />
  )
}

const MapMarkers = React.memo(
  ({ locationPoints, locationPointTypes, onClick }) => {
    if (!locationPoints) return null

    const getIcon = (type, dateCreated) => {
      const scaledSize = new window.google.maps.Size(20, 30)
      const model = locationPointTypes[type]
      if (!model) return null
      const raw = model.iconId
      const iconId = Number.isInteger(raw) && raw >= 0
        ? (raw <= MAX_MARKER_ICON_ID ? raw : ((raw - 1) % MAX_MARKER_ICON_ID) + 1)
        : 23
      const isOld = model.aging !== false && isDated(model.aging, dateCreated)
      return {
        url: isOld
          ? require(`../../assets/markers/${iconId}-aging.svg`)
          : require(`../../assets/markers/${iconId}.svg`),
        scaledSize,
      }
    }

    return locationPoints.map((point) => {
      if (!locationPointTypes[point.type]) return null
      return (
        <Marker
          key={point.id}
          position={{
            lat: point.coords[0],
            lng: point.coords[1],
          }}
          onClick={() => onClick(point)}
          icon={getIcon(point.type, point.dateCreated)}
        />
      )
    })
  },
)
// https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi3_hdpi.png

function Map({
  locationPoints,
  initialCoordinates,
  locationPointTypes,
  project,
  showRemoved,
  userId,
  isLoading,
  setLoading,
}) {
  const [selectedPoint, setSelectedPoint] = useState(null)
  const [locPointData, setLocPointData] = useState(null)
  const [authorInfo, setAuthorInfo] = useState(null)
  const [removerInfo, setRemoverInfo] = useState(null)
  const [newCoordinates, setNewCoordinates] = useState(null)
  const [isDeleteDialogVisible, showDeleteDialog] = useState(false)
  const [newPointType, setNewPointType] = useState(null)
  const [isEditing, setEditing] = useState(false)
  const [isSearching, setSearching] = useState(false) // Affects page mobile version only
  const [isFullscreen, _setFullscreen] = useState(false) // mobile version only
  const [searchLocation, setSearchLocation] = useState(null)
  const [zoom, setZoom] = useState(5)
  const [center, setCenter] = useState({
    lat: initialCoordinates[0],
    lng: initialCoordinates[1],
  })

  const prevPoints = usePrevious(locationPoints)
  useEffect(() => {
    // Clear selection when data changes
    if (prevPoints !== locationPoints) {
      resetSelection()
    }
  })

  const resetSelection = () => {
    setSelectedPoint(null)
    setLocPointData(null)
    setAuthorInfo(null)
    setRemoverInfo(null)
  }

  const locPointDataReceived = (data) => {
    UserInfo.getInfo(data.author).then(setAuthorInfo).catch(console.error)
    if (showRemoved) {
      UserInfo.getInfo(data.remover).then(setRemoverInfo).catch(console.error)
    }
    setLocPointData(data)
  }

  const onMapLoad = React.useCallback((map) => {
    map.addListener('zoom_changed', () => {
      const z = map.getZoom()
      if (z != null) setZoom(z)
    })
  }, [])

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
      }}
      center={center}
      zoom={zoom}
      onLoad={onMapLoad}
      onClick={(e) => {
        setSearchLocation(undefined)
        if (selectedPoint || newCoordinates) {
          resetSelection()
          setNewCoordinates(null)
        } else if (e.latLng) {
          setNewCoordinates({
            lat: typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat,
            lng: typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng,
          })
        }
      }}
    >
      {/* <MarkerClusterer
				// onClick={() => console.log("HELLO")}
				averageCenter
				enableRetinaIcons
				gridSize={24}
			> */}
      <MapMarkers
        locationPoints={locationPoints}
        locationPointTypes={locationPointTypes}
        onClick={React.useCallback(
          (point) => {
            resetSelection()
            setSelectedPoint(point)
            if (showRemoved)
              project
                .getRemovedLocationPointData(point.id)
                .then(locPointDataReceived)
            else
              project.getLocationPointData(point.id).then(locPointDataReceived)
          },
          [showRemoved],
        )}
      />
      {/* </MarkerClusterer> */}
      {newCoordinates && (
        <span>
          {/* <Marker
						position={newCoordinates}
						icon={getIcon()}
					/> */}
          <InfoWindow
            position={newCoordinates}
            onCloseClick={() => {
              setNewCoordinates(null)
            }}
          >
            <NewPointInfo
              types={locationPointTypes}
              onTypeSelected={(type) => setNewPointType(type)}
            />
          </InfoWindow>
        </span>
      )}
      {selectedPoint && (
        <InfoWindow
          onCloseClick={() => {
            resetSelection()
          }}
          position={{
            lat: selectedPoint.coords[0],
            lng: selectedPoint.coords[1],
          }}
        >
          {/* HACK: Redux context doesn't work under InfoWindow so we had to configure the redux store provider here */}
          <Provider store={store}>
            <InfoView
              isLoading={isLoading}
              point={selectedPoint}
              locPointData={locPointData}
              model={locationPointTypes[selectedPoint.type]}
              showRemoved={showRemoved}
              authorInfo={authorInfo}
              removerInfo={removerInfo}
              onEdit={() => setEditing(true)}
              showDeleteDialog={() => showDeleteDialog(true)}
              onRestorePoint={() => {
                setLoading(true)
                project
                  .restorePoint(selectedPoint, locPointData)
                  .then(() => {
                    setLoading(false)
                    resetSelection()
                  })
                  .catch((err) => {
                    setLoading(false)
                    console.error(err)
                  })
              }}
            />
          </Provider>
        </InfoWindow>
      )}
      {((newPointType && newCoordinates) ||
        (isEditing && selectedPoint && locPointData)) && (
        <NewPointModal
          locPointData={isEditing ? locPointData : undefined}
          inEdit={isEditing ? selectedPoint : undefined}
          type={
            isEditing
              ? locationPointTypes[selectedPoint.type]
              : locationPointTypes[newPointType]
          }
          coordinates={newCoordinates}
          onDoneEditing={() => {
            setLocPointData(null)
            project
              .getLocationPointData(selectedPoint.id)
              .then(locPointDataReceived)
            setEditing(false)
          }}
          onClose={() => {
            setNewPointType(null)
            setNewCoordinates(null)
            setEditing(false)
          }}
        />
      )}
      {isSearching && (
        <BorderlessButton
          className="map-search-cancel-button"
          icon={<ArrowLeftIcon className="icon-medium" />}
          onClick={() => setSearching(false)}
          type={BorderlessButton.Type.WHITE}
          shadowType={BorderlessButton.ShadowType.NONE}
        />
      )}
      <BorderlessButton
        className="map-search-button"
        icon={<SearchIcon className="icon-medium" />}
        onClick={() => setSearching(true)}
        type={BorderlessButton.Type.WHITE}
        shadowType={BorderlessButton.ShadowType.NONE}
      />
      {!isSafari() && (
        <BorderlessButton
          className={classNames('map-fullscreen-button', {
            'map-is-fullscreen': isFullscreen,
          })}
          icon={
            isFullscreen ? (
              <CompressIcon className="icon-medium" />
            ) : (
              <ExpandIcon className="icon-medium" />
            )
          }
          onClick={() => {
            const fullscreen = isFullscreen
            setFullscreen(!fullscreen)
              .then(() => _setFullscreen(!fullscreen))
              .catch(() => {})
          }}
          type={BorderlessButton.Type.WHITE}
          shadowType={BorderlessButton.ShadowType.NONE}
        />
      )}
      <Autocomplete
        className={classNames('map-search-bar', {
          'map-search-bar--searching': isSearching,
        })}
        placeholder="Kirjoita sijainti"
        onPlaceSelected={(place) => {
          if (place.geometry) {
            const loc = {
              coordinates: place.geometry.location,
              name: place.formatted_address,
            }
            setSearchLocation(loc)
            setCenter(loc.coordinates)
            setZoom(getPlaceZoom(place))
            // TODO: Display
          }
        }}
        options={{ types: 'locality' }}
        componentRestrictions={{ country: 'fi' }}
      />
      {searchLocation && <Marker position={searchLocation.coordinates} />}
      <Dialog
        title="Poista piste"
        message="Haluatko varmasti poistaa pisteen?"
        neutralButtonText="Peruuta"
        negativeButtonText="Poista"
        onClose={() => showDeleteDialog(false)}
        //onNeutralButtonClick={() => showDeleteDialog(false)}
        onNegativeButtonClick={() => {
          //TODO: If listener is set instead of single get - deleted points will disappear automatically ----- Might cause other problems (when image is removed updates the object here?)
          setLoading(true)
          project
            .movePointToRemoved(selectedPoint, locPointData, userId)
            .then(() => {
              setLoading(false)
              resetSelection()
              showDeleteDialog(false)
            })
            .catch(() => {
              alert('Pisteen poistaminen epäonnistui')
              showDeleteDialog(false)
              setLoading(false)
            })
        }}
        show={isDeleteDialogVisible}
      />
    </GoogleMap>
  )
}

const NavBar = ({ onBack, title, onExportCSV }) => {
  return (
    <div className="map-navbar">
      <div>
        <BorderlessButton
          className="navbar-icon"
          tier={1}
          icon={<ArrowLeftIcon className="icon-medium" />}
          onClick={onBack}
          shadowType={BorderlessButton.ShadowType.TEXT_SHADOW}
        />
        <h4 style={{ marginBottom: 0, color: '#fff', paddingLeft: '24px' }}>
          {title}
        </h4>
        <BorderlessButton
          title="lataa csv"
          className="navbar-icon map-csv-button"
          fontSize={'16px'}
          icon={<CSVIcon className="icon-medium" />}
          onClick={onExportCSV}
        />
      </div>
    </div>
  )
}

const RemovedModal = ({ showRemoved, onDone, onCancel }) => {
  const [dateFrom, setDateFrom] = useState(showRemoved.from)
  const [dateTo, setDateTo] = useState(showRemoved.to)

  return (
    <div className="bg-fade-view-12345">
      <div className="map-inner-view removed-points-modal">
        <h4>Hae poistettuja aikaväliltä:</h4>
        <div className="removed-points-date-pickers">
          <div>
            <p>Alkaen:</p>
            <DatePicker
              style={{ width: '100%' }}
              selected={dateFrom}
              onChange={(date) => setDateFrom(getBeginningOfDate(date, true))}
              dateFormat="d.M.yyyy"
              maxDate={dateTo ? new Date(dateTo) : undefined}
            />
          </div>
          <div>
            <p>Päättyen:</p>
            <DatePicker
              style={{ width: '100%' }}
              selected={dateTo}
              onChange={(date) => setDateTo(getEndOfDate(date, true))}
              dateFormat="d.M.yyyy"
              minDate={dateFrom ? new Date(dateFrom) : undefined}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            noMargin
            style={{ marginTop: '20px' }}
            title="Hae"
            onClick={() => {
              onDone({ from: dateFrom, to: dateTo })
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type={Button.Type.SECONDARY}
            noMargin
            style={{ marginTop: '12px' }}
            title="Peruuta"
            onClick={onCancel}
          />
        </div>
      </div>
    </div>
  )
}

const NewPointInfo = ({ types, onTypeSelected }) => {
  return (
    <div>
      {types &&
        Object.values(types).map((type) => {
          if (type.isLocked) return null
          return (
            <div
              key={type.id}
              className="new-marker-row"
              onClick={() => onTypeSelected(type.id)}
            >
              <div
                className="marker"
                style={{ backgroundColor: getIconColor(type.iconId) }}
              />
              <p>{type.iconId === 0 ? 'Huomio' : type.name}</p>
              <div style={{ flexGrow: 1 }} />
            </div>
          )
        })}
    </div>
  )
}

const isDated = (days, dateCreated) => {
  const interval = 86400 * days * 1000 // interval in ms
  const currentDate = new Date().getTime()
  const comparisonDate = dateCreated + interval
  return currentDate > comparisonDate
}

const getPlaceZoom = (place) => {
  let zoom = 0
  for (const ac of place.address_components) {
    let maxZoom = 0
    ac.types.forEach((type) => {
      switch (type) {
        case 'country':
          maxZoom = 6
          break
        case 'administrative_area_level_1':
          maxZoom = 9
          break
        case 'locality':
        case 'administrative_area_level_2':
          maxZoom = 13
          break
        case 'route':
          maxZoom = 16
          break
        case 'administrative_area_level_3':
        case 'street_address':
        case 'street_number':
          maxZoom = 17
          break
      }
    })
    if (maxZoom > zoom) zoom = maxZoom
  }
  return zoom
}
